import { getColorByIndex } from './common';

const STEP_DEFAULT_VALUE = 0.02;
//const DIST_DEFAULT_VALUE = 0.1;

export default class SeqTree {
  constructor(d) {
    this.data = d;
    this.innerNodes = [];
    this.__setInnerNodes(this.data);
    this.root = this.data;
    this.leaves = [];
    this.__setLeaves(this.data);
    this.__setMaxDivergenceRate();
    this.__setDistDefaultValue();
    this.__setMainConsensus();
    this.__setAllSequences();
  }
  /**
   * return the root id of the tree
   */
  getId() {
    return this.data.id;
  }
  /**
   * return the tree object
   */
  getData() {
    return this.data;
  }

  getInnerNodes() {
    return this.innerNodes;
  }

  getLeaves() {
    return this.leaves;
  }

  getRoot() {
    return this.root;
  }

  /**
   * maximal value for divergence rate
   */
  getMaxDivergenceRate() {
    return this.maxDivergenceRate;
  }

  /**
   * default value for the convergence rate
   */
  getDistDefaultValue() {
    return this.distDefaultValue;
  }

  /**
   * Step for the slidder use to change the MinimumDivergenceRate
   */
  getStepDefaultValue() {
    return STEP_DEFAULT_VALUE;
  }

  /**
   * return the number of leaves in the tree
   */
  getLeafNumber() {
    return this.leaves.length;
  }

  /**
   * return the number of nodes in all the tree
   */
  getNodeNumber() {
    return this.innerNodes.length + 1;
  }

  /**
   * return the metadata of the root of the tree
   */
  getRootMetadata() {
    return this.data.metadata;
  }

  getMainConsensus() {
    return this.mainConsensus;
  }

  getAllSequences() {
    return this.allSequences;
  }

  getCollapsedNodeIds(minDist) {
    return this.innerNodes
      .filter(node => this.getDivergenceRate(node) < minDist)
      .map(node => node.id);
  }

  /**
   * Inits the inner nodes array
   */
  __setInnerNodes(node) {
    if (!this.isLeaf(node) && !this.isRoot(node)) {
      this.innerNodes.push(node);
    }
    if (!this.isLeaf(node)) {
      node.branchset.forEach(br => this.__setInnerNodes(br));
    }
  }

  /**
   * Inits the leaves array
   */
  __setLeaves(node) {
    if (this.isLeaf(node)) {
      this.leaves.push(node);
    }
    if (!this.isLeaf(node)) {
      node.branchset.forEach(br => this.__setLeaves(br));
    }
  }

  /**
   * Sets the max divergence rate
   */
  __setMaxDivergenceRate() {
    const distancesArray = this.innerNodes.map(node => this.getDivergenceRate(node));
    let maxdist = Math.max(...distancesArray);
    this.maxDivergenceRate = Math.ceil(maxdist * 1000) / 1000;
  }

  __setDistDefaultValue() {
    /*let defValue = DIST_DEFAULT_VALUE;
    let max = this.getMaxDivergenceRate();
    if (max <= defValue) defValue = max / 2;
    this.distDefaultValue = defValue;*/
    this.distDefaultValue = 0;
  }

  __setMainConsensus() {
    this.mainConsensus = this.getConsensusSeqObject(this.data);
  }

  /**
   * Returns all the sequence objects from the root
   */
  __setAllSequences() {
    let a_sequences = this.leaves.map(l => this.getSeqObject(l));
    a_sequences.push(this.getConsensusSeqObject(this.root));
    this.allSequences = a_sequences;
  }

  /**
   * return the number of nodes under the node o
   */
  getNodeNumberUnderBranch(o) {
    if (this.isLeaf(o)) return 0;
    else {
      // If the node is collapsed, the leaves are in "_branchset"
      const key = o.branchset == null ? '_branchset' : 'branchset';
      return o[key].map(branch => this.getNodeNumberUnderBranch(branch)).reduce((a, b) => a + b, 1);
    }
  }
  /**
   * return true if the object o is a leaf
   */
  isLeaf(o) {
    if (o.hasOwnProperty('branchset')) return false;
    else return true;
  }
  isRoot(o) {
    if (o.hasOwnProperty('id') && o.id === 'NODE_1') {
      return true;
    }
    return false;
  }
  /**
   * return the first leaf in the tree o
   */
  getFirstLeaf(o) {
    if (this.isLeaf(o)) return o;
    else {
      // If the node is collapsed, the leaves are in "_branchset"
      const key = o.branchset == null ? '_branchset' : 'branchset';
      return this.getFirstLeaf(o[key][0]);
    }
  }
  /**
   *  return the first leaf object of the tree o whose ids is included in the list l
   * if no found, return null
   */
  getFirstLeafOfTheList(o, list) {
    if (this.isLeaf(o)) {
      if (list.includes(o.id)) {
        return o;
      } else return null;
    } else {
      const key = o.branchset == null ? '_branchset' : 'branchset';

      const aa = o[key].map(e => this.getFirstLeafOfTheList(e, list)).filter(v => v !== null);
      if (aa.length > 0) {
        return aa[0];
      } else return null;
    }
  }

  /**
   * return the number of leaves in the branch o
   * TODO: compter le nb de seq dans 'sequences' au lieu de retourner 1 ?
   */
  getLeafNumberUnderBranch(o) {
    if (this.isLeaf(o)) return 1;
    else {
      // If the node is collapsed, the leaves are in "_branchset"
      const key = o.branchset == null ? '_branchset' : 'branchset';
      return o[key].map(branch => this.getLeafNumberUnderBranch(branch)).reduce((a, b) => a + b, 0);
    }
  }
  /**
   * return the branch object which has the id 'id'
   */
  getBranch(id) {
    return this.getBranchInObject(this.data, id);
  }
  /**
   * Search in the object o, the branch object which has the id 'id' and return it
   */
  getBranchInObject(o, id) {
    if (o.id === id) {
      return o;
    } else {
      if (!this.isLeaf(o)) {
        // If the node is collapsed, the leaves are in "_branchset"
        const key = o.branchset == null ? '_branchset' : 'branchset';
        let aa = o[key].map(branch => this.getBranchInObject(branch, id)).filter(a => a != null);
        if (aa.length > 0) {
          return aa[0];
        } else {
          return null;
        }
      } else {
        return null;
      }
    }
  }

  /**
   * return the divergence rate of the node (=branchLength value)
   * @param {*} n
   */
  getDivergenceRate(n) {
    let rate = n.branchLength;
    if (rate < 0) rate = 0;
    return rate * 100;
  }
  /**
   * Browse the entire tree and build an array of sequences.
   * The closed sequences (dist < maxdist) are collapsed in a consensus sequence.
   * Tje sequence object are saved in seqs
   */
  __getSequenceByDist(tree, maxDist, seqs) {
    // 1) A leaf: load the sequence
    if (this.isLeaf(tree)) {
      seqs.push(this.getSeqObject(tree));
    }
    // 2) The root of the tree: the DivergenceRate value is equal to 0, so need to catch that case
    else if (this.isRoot(tree)) {
      if (this.getMaxDivergenceRate() - this.getStepDefaultValue() <= maxDist) {
        // this node needs being collapsed => build a seq object from this node
        seqs.push(this.getCollapsedSeqObject(tree));
      } else {
        // no need collapsing => explore the branches of the current node
        // If the node is collapsed, the leaves are in "_branchset"
        const key = tree.branchset == null ? '_branchset' : 'branchset';
        tree[key].forEach(n => this.__getSequenceByDist(n, maxDist, seqs));
      }
    }
    // 3) A node in the tree
    else {
      // this node needs being collapsed => build a seq object from this node
      if (this.getDivergenceRate(tree) < maxDist) {
        seqs.push(this.getCollapsedSeqObject(tree));
      } else {
        // no need collapsing => explore the branches of the current node
        const key = tree.branchset == null ? '_branchset' : 'branchset';
        tree[key].forEach(n => this.__getSequenceByDist(n, maxDist, seqs));
      }
    }
  }
  /**
   * Browse the entire tree and build an array of sequences.
   * The closed sequences (dist < maxdist) are collapsed in a consensus sequence.
   * Tje sequence object are saved in seqs
   */
  __getSequenceByDistAndRenamed(tree, maxDist, seqs, l) {
    // 1) A leaf: load the sequence
    if (this.isLeaf(tree)) {
      seqs.push(this.getSeqObject(tree));
    }
    // 2) The root of the tree: the DivergenceRate value is equal to 0, so need to catch that case
    else if (this.isRoot(tree)) {
      if (this.getMaxDivergenceRate() - this.getStepDefaultValue() <= maxDist) {
        // this node needs being collapsed => build a seq object from this node
        seqs.push(this.getCollapsedSeqObjectAndRename(tree, l));
      } else {
        // no need collapsing => explore the branches of the current node
        tree.branchset.forEach(n => this.__getSequenceByDistAndRenamed(n, maxDist, seqs, l));
      }
    }
    // 3) A node in the tree
    else {
      // this node needs being collapsed => build a seq object from this node
      if (this.getDivergenceRate(tree) < maxDist) {
        seqs.push(this.getCollapsedSeqObjectAndRename(tree, l));
      } else {
        // no need collapsing => explore the branches of the current node
        // If the node is collapsed, the leaves are in "_branchset"
        const key = tree.branchset == null ? '_branchset' : 'branchset';
        tree[key].forEach(n => this.__getSequenceByDistAndRenamed(n, maxDist, seqs, l));
      }
    }
  }

  /**
   * Browse the entire tree.
   * Save thes values in the array distarray
   */
  __getAndColorSequenceByDist(tree, maxDist, seqs, color, i) {
    // 1) a leaf: build a sequence object. Color it if 'color' param is defined.
    if (this.isLeaf(tree)) {
      let seq = this.getSeqObject(tree);
      if (color != '') {
        seq.color = color;
      }
      seqs.push(seq);
    }
    // 2) The root of the tree: the DivergenceRate value is equal to 0, so need to catch that case
    else if (this.isRoot(tree)) {
      if (this.getMaxDivergenceRate() - this.getStepDefaultValue() <= maxDist && color == '') {
        i[0] = (i[0] + 1) % 2;
        color = getColorByIndex(i[0]);
      }
      // explore the subtree
      tree.branchset.forEach(n => this.__getAndColorSequenceByDist(n, maxDist, seqs, color, i));
    }
    // 3) A node in the tree
    else {
      // This node needs being collapsed => choose a new color
      // and propagate it the sub tree
      if (this.getDivergenceRate(tree) < maxDist && color == '') {
        i[0] = (i[0] + 1) % 2;
        color = getColorByIndex(i[0]);
      }
      // explore the subtree
      // If the node is collapsed, the leaves are in "_branchset"
      const key = tree.branchset == null ? '_branchset' : 'branchset';
      tree[key].forEach(n => this.__getAndColorSequenceByDist(n, maxDist, seqs, color, i));
    }
  }

  /**
   * Build a sequence object from a leave object.
   * The created object contains the values: id, name, seq, isConsensus, isNode values
   *note: the object o has to be a leave on the tree *
   */
  getSeqObject(o) {
    if (!o.hasOwnProperty('sequence')) {
      console.error('Object ' + o + " should contain a 'sequence'.");
      return null;
    }
    const c = {
      id: o.id,
      name: o.name,
      seq: o.sequence,
      leave_number: o.leave_number,
      isConsensus: false,
      isNode: false,
      color: '',
    };
    return c;
  }

  /**
   * Build a sequence object from a branch. The sequence is the consensus.
   * The created object contains the values: id, name, seq, isConsensus, isNode
   * note: the o parameter has to be a branch on the tree
   */
  getCollapsedSeqObject(o) {
    if (!o.hasOwnProperty('sequence')) {
      console.error('Object should contain a sequence value', o);
      return null;
    }
    const c = {
      id: o.id,
      name: '#' + this.getLeafNumberUnderBranch(o) + '_' + this.getFirstLeaf(o).name,
      seq: o.sequence,
      isConsensus: true,
      isNode: true,
      color: '',
    };
    return c;
  }

  /**
   *
   * @param {*} o current node
   * @param {*} l list of leaf IDs: if the current node has a leaf included in that list,
   * rename the collapsed sequence with the name of this leaf
   */
  getCollapsedSeqObjectAndRename(o, l) {
    if (!o.hasOwnProperty('sequence')) {
      console.error('Object should contain a sequence value', o);
      return null;
    }

    const c = {
      id: o.id,
      name: this.__getNewNameOfConsensusFromList(o, l),
      seq: o.sequence,
      isConsensus: true,
      isNode: true,
      color: '',
    };
    return c;
  }

  /**
   * The name of an inner node is changed if the list contains
   * one of its descendant leaves
   * @param {} o
   * @param {*} l
   */
  __getNewNameOfConsensusFromList(o, l) {
    if (!o.hasOwnProperty('sequence')) {
      console.error('Object should contain a sequence value', o);
      return null;
    }
    let element = this.getFirstLeafOfTheList(o, l);
    if (element === null) element = this.getFirstLeaf(o);

    return '#' + this.getLeafNumberUnderBranch(o) + '_' + element.name;
  }

  /**
   * Build a consensus sequence object from a branch. The sequence is the consensus.
   * The created object contains the values: id, name, seq, isConsensus, isNode
   * note: the object o has to be a branch on the tree
   */
  getConsensusSeqObject(o) {
    if (!o.hasOwnProperty('sequence')) {
      console.error('Object ' + o + " should contain a 'sequence' value.");
      return null;
    }
    const c = {
      id: o.id + '_consensus',
      name: '#' + this.getLeafNumberUnderBranch(o) + '_consensus',
      seq: o.sequence,
      isConsensus: true,
      isNode: false,
      color: '',
    };
    //if (o.hasOwnProperty('metadata')) c.metadata = o.metadata;

    return c;
  }

  /**
   * get the id of the parent of the node 'id'
   */
  getParentNodeId(id) {
    return this.getParent(id, this.data, '');
  }
  /**
   * search the parent of the node 'id' and return its id
   * TODO: a reecrire. doit etre simplifiable!!
   */
  getParent(id, currentNode, parentNodeId) {
    if (currentNode.id === id) {
      if (parentNodeId === '') {
        return id;
      } else {
        return parentNodeId;
      }
    } else {
      if (currentNode.hasOwnProperty('branchset')) {
        // If the node is collapsed, the leaves are in "_branchset"
        const key = currentNode.branchset == null ? '_branchset' : 'branchset';
        let aa = currentNode[key]
          .map(node => this.getParent(id, node, currentNode.id))
          .filter(a => a !== '');
        if (aa.length > 0) {
          return aa[0];
        } else {
          return '';
        }
      } else {
        return '';
      }
    }
  }

  /**
   * Returns the sequence objects from the node 'id'.
   * Collapse node with a distance < mindist
   * @param {*} id Node id
   * @param {*} mindist collapse node with a distance < to mindist
   * @param {*} colorInsteadOfCollapse Boolean. If true, dont collapse node but only color close nodes with the same color
   */
  getSequencesWithMinDist(id, mindist, colorInsteadOfCollapse) {
    let seqs = [];
    let tree = this.getBranch(id);
    let i = [0];
    if (colorInsteadOfCollapse) {
      this.__getAndColorSequenceByDist(tree, mindist, seqs, '', i);
    } else {
      this.__getSequenceByDist(tree, mindist, seqs);
    }

    // add the consensus sequence
    let consensus = this.getConsensusSeqObject(tree);
    seqs.push(consensus);
    return seqs;
  }

  getSequencesWithMinDistAndRenameConsensus(id, mindist, colorInsteadOfCollapse, l) {
    let seqs = [];
    let tree = this.getBranch(id);
    let i = [0];
    if (colorInsteadOfCollapse) {
      this.__getAndColorSequenceByDist(tree, mindist, seqs, '', i);
    } else {
      this.__getSequenceByDistAndRenamed(tree, mindist, seqs, l);
    }

    // add the consensus sequence
    let consensus = this.getConsensusSeqObject(tree);
    seqs.push(consensus);

    return seqs;
  }

  getLeaveIds(id) {
    let seqs = [];
    let tree = this.getBranch(id);
    this.__getSequenceByDist(tree, 0, seqs);
    return seqs.map(s => s.id);
  }

  filterRegions(regions, id) {
    return regions.filter(r => r.id === id);
  }

  /**
   * Change the name of inner nodes in this way :
   * #(number of leaves that are descendants of the node)+first leaf of the list.
   * @param  list a list of selected leave ids
   */
  renameNodeConsensusFromList(list) {
    this.innerNodes = this.innerNodes.map(node => {
      node.name = this.__getNewNameOfConsensusFromList(node, list);
      return node;
    });
  }
}
